import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import {
  CreditCard,
  Calendar,
  DollarSign,
  Upload,
  CheckCircle,
  AlertCircle,
  Receipt,
  Home,
  User,
  FileText,
  Building,
  RefreshCw,
  Users,
  Wallet
} from 'lucide-react';

const ModalMakePaymentForClientRentalsForm = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;
  const [houseNumberPOP, setHouseNumberPOP] = useState("");
  const [datePOPHouses, setDatePOPHouses] = useState("");
  const [amountPOPHouses, setAmountPOPHouses] = useState("");
  const [POPHouses, setPOPHouses] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [companyID, setCompanyID] = useState("");
  const [businessLocationID, setBusinessLocationID] = useState("");
  const [houseOptions, setHouseOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [selectedHouseDetails, setSelectedHouseDetails] = useState(null);
  const [businessLocations, setBusinessLocations] = useState([]);

  useEffect(() => {
    fetchBusinessLocations();
  }, []);

  useEffect(() => {
    if (businessLocationID) {
      fetchHouseOptions();
    }
  }, [businessLocationID]);

  const fetchBusinessLocations = async () => {
    const formData = new FormData();
    formData.append("apiType", 'getBusinessLocationOnUploadForm');
    formData.append("companyID", 1);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setBusinessLocations(response.data);
    } catch (error) {
      console.error("Error fetching business locations:", error);
    }
  };

  const fetchHouseOptions = async () => {
    const formData = new FormData();
    formData.append("apiType", 'getPOPHousesOnUploadForm');
    formData.append("businessLocationID", businessLocationID);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setHouseOptions(response.data);
    } catch (error) {
      console.error("Error fetching house options:", error);
    }
  };

  const handleInputChange = (e) => {
    const { id, value, files } = e.target;
    
    if (id === "POPHouses") {
      const file = files[0];
      setPOPHouses(file);
      if (file) {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setFilePreview({ type: 'image', url: reader.result });
          };
          reader.readAsDataURL(file);
        } else if (file.type === 'application/pdf') {
          setFilePreview({ type: 'pdf', url: URL.createObjectURL(file) });
        }
      } else {
        setFilePreview(null);
      }
    } else if (id === "houseNumberPOP") {
      setHouseNumberPOP(value);
      const house = houseOptions.find(h => h.rentalsID === value);
      setSelectedHouseDetails(house);
    } else if (id === "datePOPHouses") {
      setDatePOPHouses(value);
    } else if (id === "amountPOPHouses") {
      setAmountPOPHouses(value);
    } else if (id === "businessLocationID") {
      setBusinessLocationID(value);
      setHouseNumberPOP("");
      setSelectedHouseDetails(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZM', { style: 'currency', currency: 'ZMW' }).format(amount);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus({ type: '', message: '' });

    if (!houseNumberPOP || !datePOPHouses || !amountPOPHouses || !POPHouses) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Please fill all required fields and upload proof of payment.' 
      });
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("houseNumberPOP", houseNumberPOP);
    formData.append("datePOPHouses", datePOPHouses);
    formData.append("amountPOPHouses", amountPOPHouses);
    formData.append("POPHouses", POPHouses);
    formData.append("companyID", companyID);
    formData.append("businessLocationID", businessLocationID);
    formData.append("apiType", 'uploadPOPHouses');

    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSubmitStatus({ 
        type: 'success', 
        message: 'Rental payment uploaded successfully!' 
      });
      
      // Reset form
      setHouseNumberPOP("");
      setDatePOPHouses("");
      setAmountPOPHouses("");
      setPOPHouses(null);
      setFilePreview(null);
      setSelectedHouseDetails(null);
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Error: Please check your internet connection. If this persists, contact support.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = houseNumberPOP && datePOPHouses && amountPOPHouses && POPHouses;

  return (
    <>
      <Header />
      
      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 pl-64">
        <div className="p-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                <Wallet className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Rental Payments</h1>
                <p className="text-gray-600 mt-2">Upload proof of payment for house tenants</p>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Payment Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  {/* Form Header */}
                  <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center space-x-3">
                      <Upload className="w-6 h-6 text-blue-500" />
                      <h2 className="text-xl font-bold text-gray-900">New Rental Payment</h2>
                    </div>
                    <p className="text-gray-600 mt-2">Record rental payment with proof of payment document</p>
                  </div>

                  {/* Form Content */}
                  <div className="p-8">
                    {submitStatus.message && (
                      <div className={`mb-6 p-4 rounded-xl ${
                        submitStatus.type === 'success' 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex items-center">
                          {submitStatus.type === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                          )}
                          <p className={submitStatus.type === 'success' ? 'text-green-700' : 'text-red-700'}>
                            {submitStatus.message}
                          </p>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Business Location */}
                      <div>
                        <label htmlFor="businessLocationID" className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center">
                            <Building className="w-4 h-4 mr-2 text-gray-400" />
                            Business Location *
                          </div>
                        </label>
                        <select
                          id="businessLocationID"
                          value={businessLocationID}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                          required
                        >
                          <option value="">Select location...</option>
                          {businessLocations.map(location => (
                            <option key={location.businessLocationID} value={location.businessLocationID}>
                              {location.businessLocationName}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* House Selection */}
                      {businessLocationID && (
                        <div>
                          <label htmlFor="houseNumberPOP" className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center">
                              <Home className="w-4 h-4 mr-2 text-gray-400" />
                              House Details *
                            </div>
                          </label>
                          <select
                            id="houseNumberPOP"
                            value={houseNumberPOP}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                            required
                          >
                            <option value="">Select house...</option>
                            {houseOptions.map(option => (
                              <option key={option.rentalsID} value={option.rentalsID}>
                                Block {option.block} - House {option.houseNumber} - {option.tenantNameDB}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Selected House Details */}
                      {selectedHouseDetails && (
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-gray-600">Block</p>
                              <p className="font-medium text-gray-900">{selectedHouseDetails.block}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">House Number</p>
                              <p className="font-medium text-gray-900">{selectedHouseDetails.houseNumber}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Tenant</p>
                              <p className="font-medium text-gray-900">{selectedHouseDetails.tenantNameDB}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-600">Location</p>
                              <p className="font-medium text-gray-900">{selectedHouseDetails.businessLocationName}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Date and Amount */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="datePOPHouses" className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                              Payment Date *
                            </div>
                          </label>
                          <input
                            type="date"
                            id="datePOPHouses"
                            value={datePOPHouses}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                            required
                          />
                        </div>

                        <div>
                          <label htmlFor="amountPOPHouses" className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center">
                              <DollarSign className="w-4 h-4 mr-2 text-gray-400" />
                              Amount (ZMW) *
                            </div>
                          </label>
                          <input
                            type="number"
                            id="amountPOPHouses"
                            value={amountPOPHouses}
                            onChange={handleInputChange}
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                            required
                          />
                        </div>
                      </div>

                      {/* Proof of Payment Upload */}
                      <div>
                        <label htmlFor="POPHouses" className="block text-sm font-medium text-gray-700 mb-2">
                          <div className="flex items-center">
                            <Receipt className="w-4 h-4 mr-2 text-gray-400" />
                            Proof of Payment *
                          </div>
                        </label>
                        
                        <div className="space-y-4">
                          {filePreview && (
                            <div className="border border-gray-200 rounded-xl p-4">
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-sm font-medium text-gray-700">File Preview</p>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPOPHouses(null);
                                    setFilePreview(null);
                                  }}
                                  className="text-red-500 hover:text-red-700 text-sm"
                                >
                                  Remove
                                </button>
                              </div>
                              {filePreview.type === 'image' && (
                                <img 
                                  src={filePreview.url} 
                                  alt="Preview" 
                                  className="w-full max-h-48 object-contain rounded-lg"
                                />
                              )}
                              {filePreview.type === 'pdf' && (
                                <div className="bg-gray-50 p-4 rounded-lg">
                                  <FileText className="w-12 h-12 text-red-500 mx-auto" />
                                  <p className="text-center mt-2 text-sm text-gray-600">PDF Document</p>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="relative">
                            <input
                              type="file"
                              id="POPHouses"
                              onChange={handleInputChange}
                              accept="image/*,.pdf,.doc,.docx"
                              className="hidden"
                              required
                            />
                            <label
                              htmlFor="POPHouses"
                              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all duration-300"
                            >
                              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                                <p className="text-sm text-gray-500">
                                  <span className="font-medium text-blue-500">Click to upload</span> or drag and drop
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                  PNG, JPG, PDF, DOC up to 10MB
                                </p>
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div className="pt-6">
                        <button
                          type="submit"
                          disabled={!isFormValid || isLoading}
                          className={`w-full py-3.5 px-6 rounded-xl font-medium transition-all duration-300 ${
                            isFormValid && !isLoading
                              ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {isLoading ? (
                            <div className="flex items-center justify-center">
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                              Uploading Payment...
                            </div>
                          ) : (
                            <div className="flex items-center justify-center">
                              <Upload className="w-5 h-5 mr-2" />
                              Upload Rental Payment
                            </div>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>

                  {/* Form Footer */}
                  <div className="px-8 py-6 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <AlertCircle className="w-4 h-4 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Supported Formats</p>
                          <p className="text-xs text-gray-500">Images, PDF, Word documents up to 10MB</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">All fields marked with * are required</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Guidelines & Information */}
              <div className="space-y-8">
                {/* Rental Guidelines */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-purple-500" />
                    Rental Payment Guidelines
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 mr-3"></div>
                      <span className="text-sm text-gray-700">Ensure payment date matches receipt date</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 mr-3"></div>
                      <span className="text-sm text-gray-700">Upload clear images of rent receipts or bank slips</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-1.5 mr-3"></div>
                      <span className="text-sm text-gray-700">Verify house and tenant details before submission</span>
                    </li>
                  </ul>
                </div>

                {/* Quick Stats */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-purple-500" />
                      Rental Statistics
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Home className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Available Houses</span>
                        </div>
                        <span className="font-medium text-gray-900">{houseOptions.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Building className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Active Locations</span>
                        </div>
                        <span className="font-medium text-gray-900">{businessLocations.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Active Tenants</span>
                        </div>
                        <span className="font-medium text-gray-900">
                          {[...new Set(houseOptions.map(h => h.tenantNameDB))].length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <RefreshCw className="w-5 h-5 mr-2 text-blue-500" />
                      Quick Actions
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="space-y-3">
                      <button 
                        onClick={() => fetchHouseOptions()}
                        className="w-full flex items-center justify-between p-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                      >
                        <span className="text-sm font-medium text-purple-700">Refresh House List</span>
                        <RefreshCw className="w-4 h-4 text-purple-500" />
                      </button>
                      <button 
                        onClick={() => setBusinessLocationID("")}
                        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <span className="text-sm font-medium text-gray-700">Clear Selection</span>
                        <AlertCircle className="w-4 h-4 text-gray-500" />
                      </button>
                      <button 
                        onClick={() => window.open('/ViewTenants', '_blank')}
                        className="w-full flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                      >
                        <span className="text-sm font-medium text-blue-700">View All Tenants</span>
                        <Users className="w-4 h-4 text-blue-500" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ModalMakePaymentForClientRentalsForm;